import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Globe, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LANGUAGES } from "@/i18n/resources";

export default function LanguageSwitcher({ compact = false }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const change = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  const sz = compact ? "h-9" : "h-11";

  return (
    <div ref={rootRef} className="relative">
      <button
        data-testid="language-switcher-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("language.switchTo")}
        aria-expanded={open}
        className={`group flex ${sz} items-center gap-2 rounded-full border border-ivory/10 px-3 text-ivory transition-colors hover:border-gold hover:text-gold`}
      >
        <Globe size={compact ? 13 : 15} strokeWidth={1.6} />
        <span className="font-sans text-[11px] uppercase tracking-widest">
          {current.code}
        </span>
      </button>

      <AnimatePresence>
        {open ? (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.76, 0, 0.24, 1] }}
            data-testid="language-switcher-menu"
            className="absolute right-0 top-full z-50 mt-2 min-w-[240px] overflow-hidden rounded-2xl border border-ivory/10 bg-ink-900/95 shadow-2xl backdrop-blur-xl"
          >
            <div className="px-4 pb-2 pt-3 font-sans text-[10px] uppercase tracking-widest text-gold">
              {t("language.label")}
            </div>
            <ul className="max-h-[60vh] overflow-y-auto pb-2">
              {LANGUAGES.map((l) => {
                const active = l.code === i18n.language;
                return (
                  <li key={l.code}>
                    <button
                      data-testid={`language-option-${l.code}`}
                      onClick={() => change(l.code)}
                      className={`flex w-full items-center justify-between gap-4 px-4 py-2.5 text-left transition-colors ${
                        active
                          ? "bg-gold/10 text-gold"
                          : "text-ivory hover:bg-ivory/[0.04] hover:text-gold"
                      }`}
                      dir={l.rtl ? "rtl" : "ltr"}
                    >
                      <span className="flex items-center gap-3">
                        <span className="font-sans text-[10px] uppercase tracking-widest text-ivory-dim">
                          {l.code}
                        </span>
                        <span className="font-display text-lg leading-none tracking-tight">
                          {l.native}
                        </span>
                      </span>
                      {active ? <Check size={14} className="shrink-0" /> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
